import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  CheckboxInput,
  DataGrid,
  Dialog,
  FilterToolbar,
  Form,
  FormButton,
  Input,
  string,
  SwitchInput,
  useNotifications,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  GetAdminAdvisoryOrganizationsParams,
  postAdminAdvisoryOrganizationIntegration,
  postAdminAdvisoryOrganizationRecordKeepingOrganization,
  useDeleteAdminAdvisoryOrganization,
  useGetAdminAdvisoryOrganizations,
  useGetAdminIntegrations,
  useGetAdminRecordKeepingOrganizations,
  usePostAdminAdvisoryOrganization
} from '@ijoin/hooks';
import { InfoOutlined } from '@mui/icons-material';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';
import { z } from 'zod';

import { AdvisoryOrgActionsCell, LinkButton } from '../../components';

type AdvisoryOrganization = {
  advisoryOrganizationId: string;
  billingRecordKeepingOrganizationName?: string;
  city?: string;
  name: string;
  state?: string;
};

type CreateFormProps = {
  addressOne: string;
  addressTwo: string;
  billingRecordKeepingOrgId: null | string;
  city: string;
  enableActLicense: boolean;
  hasInvestorView: boolean;
  hasManifest: boolean;
  hasV2Enrollment: boolean;
  integrationIds: string[];
  logoUri: string;
  name: string;
  rkoId: null | string;
  s3Namespace: string;
  state: string;
  website: string;
  zip: string;
};

const createResolver = zodResolver(
  z.object({
    addressOne: z.string(),
    addressTwo: z.string(),
    billingRecordKeepingOrgId: z.string().nullable(),
    city: z.string(),
    enableActLicense: z.boolean(),
    hasInvestorView: z.boolean(),
    hasManifest: z.boolean(),
    hasV2Enrollment: z.boolean(),
    integrationIds: z.array(z.string()),
    logoUri: z.string(),
    name: string({ maxLength: 100, required: true }),
    rkoId: z.string().nullable(),
    s3Namespace: z
      .string()
      .regex(/^[a-z0-9-]*$/, 'Lowercase letters, numbers and dashes only')
      .max(64),
    state: z.string(),
    website: z.string(),
    zip: z.string()
  })
);

const createDefaultValues: CreateFormProps = {
  addressOne: '',
  addressTwo: '',
  billingRecordKeepingOrgId: null,
  city: '',
  enableActLicense: false,
  hasInvestorView: false,
  hasManifest: false,
  hasV2Enrollment: false,
  integrationIds: [],
  logoUri: '',
  name: '',
  rkoId: null,
  s3Namespace: '',
  state: '',
  website: '',
  zip: ''
};

type RkoOption = { label: string; value: string };
type IntegrationOption = { label: string; value: string };

const filters: FilterDef[] = [
  { label: 'Search', name: 'search', type: 'text' }
];

const AdvisoryOrganizationsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const createOrg = usePostAdminAdvisoryOrganization({
    mutation: {
      onError: () =>
        notifications.error('Failed to create advisory organization'),
      onSuccess: async result => {
        const orgId = result.advisoryOrganizationId;
        const { integrationIds, rkoId } = createMethods.getValues();

        try {
          const linkPromises: Promise<unknown>[] = [];

          if (rkoId) {
            linkPromises.push(
              postAdminAdvisoryOrganizationRecordKeepingOrganization(
                orgId,
                rkoId
              )
            );
          }

          for (const integrationId of integrationIds) {
            linkPromises.push(
              postAdminAdvisoryOrganizationIntegration(orgId, integrationId)
            );
          }

          await Promise.all(linkPromises);
        } catch {
          notifications.warning(
            'Organization created but some links failed to save'
          );
        }

        queryClient.invalidateQueries({
          queryKey: ['/api/v3/admin/advisory-organizations']
        });
        setCreateDialogOpen(false);
        createMethods.reset();
        notifications.success('Advisory organization created');
        navigate({ to: `/organizations/${orgId}` });
      }
    }
  });
  const deleteOrg = useDeleteAdminAdvisoryOrganization({
    mutation: {
      onError: () =>
        notifications.error('Failed to delete advisory organization'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['/api/v3/admin/advisory-organizations']
        });
        notifications.success('Advisory organization deleted');
      }
    }
  });

  const allRkos = useGetAdminRecordKeepingOrganizations({ take: 1000 });
  const allIntegrations = useGetAdminIntegrations({ take: 1000 });

  const [createDialogOpen, toggleCreateDialogOpen, setCreateDialogOpen] =
    useToggle(false);
  const [query, setQuery] = useState<GridQuery>();

  const createMethods = useForm<CreateFormProps>({
    defaultValues: createDefaultValues,
    mode: 'all',
    resolver: createResolver
  });

  const onCreateSubmit = (data: CreateFormProps) => {
    const { integrationIds: _integrationIds, rkoId: _rkoId, ...orgData } = data;

    createOrg.mutate({
      data: {
        ...orgData,
        billingRecordKeepingOrgId: data.billingRecordKeepingOrgId ?? undefined,
        enableActLicense: data.billingRecordKeepingOrgId
          ? data.enableActLicense
          : undefined,
        s3Namespace: orgData.s3Namespace || undefined
      }
    });
  };

  const columns: GridColDef<AdvisoryOrganization>[] = [
    {
      field: 'name',
      flex: 2,
      headerName: 'Name',
      renderCell: params => (
        <LinkButton
          data-component='link'
          data-testid={camelCase(params.row.name)}
          params={{
            advisoryOrganizationId: params.row.advisoryOrganizationId
          }}
          to='/organizations/$advisoryOrganizationId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'city',
      flex: 1,
      headerName: 'City'
    },
    {
      field: 'state',
      flex: 0.75,
      headerName: 'State'
    },
    {
      field: 'billingRecordKeepingOrganizationName',
      flex: 1.5,
      headerName: 'Billing RK Org',
      sortable: false
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <AdvisoryOrgActionsCell
          onDeleted={() =>
            deleteOrg.mutate({
              advisoryOrganizationId: params.row.advisoryOrganizationId
            })
          }
          org={params.row}
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  const organizations = useGetAdminAdvisoryOrganizations(
    query as GetAdminAdvisoryOrganizationsParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const onOpenCreateDialog = () => setCreateDialogOpen(true);

  const billingRecordKeepingOrgId = createMethods.watch(
    'billingRecordKeepingOrgId'
  );

  const rkoOptions = (allRkos.data?.data ?? []).map(rko => ({
    label: rko.name,
    value: rko.recordKeepingOrganizationId
  }));

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Typography variant='h5'>Advisory Organizations</Typography>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => row.advisoryOrganizationId}
        initialSort={[{ field: 'name', sort: 'asc' }]}
        loading={!query || organizations.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        rowCount={organizations.data?.pagination?.total ?? 0}
        rows={organizations.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'advisoryOrganizations'
          },
          toolbar: {
            addLabel: 'Advisory Organization',
            addTestId: 'createAdvisoryOrganization',
            onAdd: onOpenCreateDialog,
            onRefresh: organizations.refetch,
            searchButtonTestId: 'search',
            triggerTestId: 'advisoryOrganizations-FilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      <Dialog
        data-testid='createAdvisoryOrganizationDialog'
        fullWidth
        isOpen={createDialogOpen}
        maxWidth='md'
        onClose={toggleCreateDialogOpen}>
        <Form
          methods={createMethods}
          onSubmit={createMethods.handleSubmit(onCreateSubmit)}>
          <DialogTitle variant='h3'>Create Advisory Organization</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Stack spacing={2}>
                <Typography variant='h4'>Organization Info</Typography>
                <Divider />
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <Input
                    data-testid='createOrgName'
                    format='string'
                    fullWidth
                    hideError
                    label='Name'
                    name='name'
                    required
                    size='small'
                  />
                  <Input
                    data-testid='createOrgWebsite'
                    format='string'
                    fullWidth
                    hideError
                    label='Website'
                    name='website'
                    size='small'
                  />
                </Stack>
                <Input
                  data-testid='createOrgLogoUri'
                  format='string'
                  fullWidth
                  hideError
                  label='Logo URI'
                  name='logoUri'
                  size='small'
                />
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Address</Typography>
                <Divider />
                <Input
                  data-testid='createOrgAddressOne'
                  format='string'
                  fullWidth
                  hideError
                  label='Address Line 1'
                  name='addressOne'
                  size='small'
                />
                <Input
                  data-testid='createOrgAddressTwo'
                  format='string'
                  fullWidth
                  hideError
                  label='Address Line 2'
                  name='addressTwo'
                  size='small'
                />
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <Input
                    data-testid='createOrgCity'
                    format='string'
                    fullWidth
                    hideError
                    label='City'
                    name='city'
                    size='small'
                  />
                  <Input
                    data-testid='createOrgState'
                    format='string'
                    fullWidth
                    hideError
                    label='State'
                    name='state'
                    size='small'
                  />
                  <Input
                    data-testid='createOrgZip'
                    format='string'
                    fullWidth
                    hideError
                    label='Zip'
                    name='zip'
                    size='small'
                  />
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Integrations</Typography>
                <Divider />
                <Autocomplete<RkoOption>
                  data-testid='createOrgRko'
                  fullWidth
                  getOptionValue={option => option.value}
                  hideError
                  label='Record Keeping Organization'
                  name='rkoId'
                  options={rkoOptions}
                />
                <Autocomplete<IntegrationOption>
                  data-testid='createOrgIntegrations'
                  fullWidth
                  getOptionValue={option => option.value}
                  hideError
                  label='Integrations'
                  multiple
                  name='integrationIds'
                  options={(allIntegrations.data?.data ?? []).map(i => ({
                    label: i.description,
                    value: i.integrationId
                  }))}
                />
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Settings</Typography>
                <Divider />
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <CheckboxInput
                    data-testid='createOrgHasV2Enrollment'
                    hideError
                    name='hasV2Enrollment'
                    option='Has V2 Enrollment'
                  />
                  <CheckboxInput
                    data-testid='createOrgHasManifest'
                    hideError
                    name='hasManifest'
                    option='Has Manifest'
                  />
                  <CheckboxInput
                    data-testid='createOrgHasInvestorView'
                    hideError
                    name='hasInvestorView'
                    option='Has InvestorView'
                  />
                </Stack>
                <Autocomplete<RkoOption>
                  data-testid='createOrgBillingRko'
                  fullWidth
                  getOptionValue={option => option.value}
                  hideError
                  label='Billing Record Keeper'
                  name='billingRecordKeepingOrgId'
                  options={rkoOptions}
                />
                <SwitchInput
                  data-testid='createOrgEnableActLicense'
                  disabled={!billingRecordKeepingOrgId}
                  hideError
                  label='Enable ACT Analytics'
                  name='enableActLicense'
                  tooltip={
                    billingRecordKeepingOrgId
                      ? undefined
                      : 'Select a billing record keeper to enable ACT Analytics.'
                  }
                  tooltipIcon={
                    billingRecordKeepingOrgId ? undefined : <InfoOutlined />
                  }
                />
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Sync Pipeline</Typography>
                <Divider />
                <Input
                  data-testid='createOrgS3Namespace'
                  format='string'
                  fullWidth
                  helperText='SFTP folder slug, e.g. pcs-aspire. Defaults to a slug of the name. Cannot be changed once files are flowing.'
                  label='S3 Namespace'
                  name='s3Namespace'
                  size='small'
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelCreateOrg'
              onClick={toggleCreateDialogOpen}
              variant='outlined'>
              Cancel
            </Button>
            <FormButton
              data-testid='saveCreateOrg'
              triggerValidation
              variant='contained'>
              Save
            </FormButton>
          </DialogActions>
        </Form>
      </Dialog>
    </Stack>
  );
};

export const Route = createFileRoute('/organizations/')({
  component: AdvisoryOrganizationsPage
});
